// Authored city blocks: hand-built pixel buildings (stepped roofs with equipment, 3/4-view shop façades, katakana
// neon, lanterns, street props) painted into the cached map texture. Light comes from the top-left; only south
// faces show a façade. Everything is drawn over the block's own solid tiles or flat on the surrounding ground,
// so collision never disagrees with the picture. Lit elements are reported as CityLight for additive glow sprites.
import { CITY_ART, WORLD } from '../../common/src/data';
import { GameMap, T, TILE, tileAt, inTileRect } from '../../common/src/map';
import { hash2 } from '../../common/src/util';
import { planCity } from './city-plan';

export interface CityLight { x: number; y: number; w: number; h: number; color: string; alpha: number }
export type G = CanvasRenderingContext2D;
export type Lights = CityLight[];
/** A building lot: façade kind, roof materials, tall-section rows and roof props [kind, x, y(, x2, y2)] in lot px. */
export interface Lot { x: number; w: number; front: string; roof: string; step?: number; lowRoof?: string; props: (string | number)[][] }
/** A rectangle of building tiles; `facade` rows of its south edge are drawn as the shop fronts. */
export interface Block { id: string; x: number; y: number; w: number; h: number; facade: number; lots: Lot[] }

export const P = {
  k: '#070a12', wall: '#1a2236', wallLt: '#27334b', wallDk: '#10172a',
  rim: '#8d9bb1', rimDk: '#56637a', shade: '#1f2838',
  metal: '#7d8ba0', metalLt: '#b8c3d2', metalDk: '#46536a',
  magenta: '#ff4fbc', magentaDk: '#8e2470', pink: '#ffc2ea',
  cyan: '#45e4f4', cyanDk: '#17707f', cyanLt: '#d6fbff',
  violet: '#9466ee', violetDk: '#2c1a5c',
  amber: '#ffc56b', amberDk: '#9a5f26', amberLt: '#fff1c4',
  red: '#e8433e', redDk: '#6e1820', redLt: '#ff9a84',
  white: '#eef3f8', wood: '#5a3826', woodDk: '#38211a', woodLt: '#7f5437', navy: '#1d2a58',
};
export const ROOFS: Record<string, { base: string; alt: string; seam: string; shade: string }> = {
  slate: { base: '#46566f', alt: '#4f6079', seam: '#3a485e', shade: '#2d394c' },
  tar: { base: '#333c4d', alt: '#3d4759', seam: '#283040', shade: '#20283a' },
  ribbed: { base: '#4e5c71', alt: '#61708a', seam: '#3a4659', shade: '#2b3546' },
  tile: { base: '#3b404e', alt: '#505767', seam: '#262a35', shade: '#20232d' },
};

export const px = (g: G, x: number, y: number, w: number, h: number, c: string) => { g.fillStyle = c; g.fillRect(x, y, w, h); };
export function stamp(g: G, rows: readonly string[], x: number, y: number, pal: Record<string, string>) {
  rows.forEach((r, j) => { for (let i = 0; i < r.length; i++) { const c = pal[r[i]]; if (c) px(g, x + i, y + j, 1, 1, c); } });
}
export function disc(g: G, cx: number, cy: number, r: number, c: string) {
  for (let y = -r; y <= r; y++) { const hw = Math.floor(Math.sqrt(r * r - y * y + r * 0.6)); px(g, cx - hw, cy + y, hw * 2 + 1, 1, c); }
}
/** A top-down box: outline, top face, a 2 px visible south face and a top-left highlight. */
export function box(g: G, x: number, y: number, w: number, h: number, top: string, side: string, lit: string) {
  px(g, x, y, w, h, P.k); px(g, x + 1, y + 1, w - 2, h - 2, top); px(g, x + 1, y + h - 3, w - 2, 2, side);
  px(g, x + 1, y + 1, w - 2, 1, lit); px(g, x + 1, y + 1, 1, h - 4, lit);
}

// ------------------------------------------------------------------ katakana pixel glyphs (illustrative signage)
const GLYPHS: Record<string, string[]> = {
  'カ': ['..#..', '#####', '..#.#', '.#..#', '.#..#', '#..#.'],
  'ラ': ['#####', '.....', '#####', '....#', '...#.', '.##..'],
  'オ': ['...#.', '#####', '..##.', '.#.#.', '#..#.', '..##.'],
  'ケ': ['.#...', '.####', '#..#.', '...#.', '..#..', '.#...'],
  'ゲ': ['.#..#.#', '.####..', '#..#...', '...#...', '..#....', '.#.....'],
  'ー': ['.....', '.....', '#####', '.....', '.....', '.....'],
  '｜': ['..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  'ム': ['..#..', '..#..', '.#...', '.#.#.', '#...#', '#####'],
  'キ': ['..#..', '#####', '..#..', '#####', '..#..', '..#..'],
  'ブ': ['....#.#', '####...', '...#...', '...#...', '..#....', '.#.....'],
  'ホ': ['..#..', '#####', '..#..', '.###.', '#.#.#', '..#..'],
  'テ': ['#####', '.....', '#####', '..#..', '..#..', '.#...'],
  'ル': ['.#.#.', '.#.#.', '.#.#.', '.#.#.', '.#.##', '#..#.'],
  'ン': ['#....', '.#..#', '....#', '...#.', '..#..', '##...'],
  'メ': ['....#', '.#..#', '..##.', '..#..', '.#.#.', '#....'],
  'コ': ['#####', '....#', '....#', '....#', '#####', '.....'],
  'チ': ['..###', '###..', '#####', '..#..', '..#..', '.#...'],
  'バ': ['....#.#', '.#.#...', '.#..#..', '.#..#..', '#....#.', '#....#.'],
  'パ': ['.....#.', '.#.#.#.', '.#.#...', '.#..#..', '#...#..', '#....#.'],
  // district expansion: Okubo market and Nishi-Shinjuku signage
  'ス': ['#####', '....#', '...#.', '..##.', '.#..#', '#...#'],
  'ヤ': ['.#...', '#####', '.#.#.', '..#..', '..#..', '..#..'],
  'ト': ['.#...', '.#...', '.###.', '.#...', '.#...', '.#...'],
  'リ': ['#..#.', '#..#.', '#..#.', '...#.', '..#..', '.#...'],
  'シ': ['#....', '.#..#', '#...#', '...#.', '..#..', '##...'],
  'ジ': ['#...#.#', '.#....#', '#...#..', '...#...', '..#....', '##.....'],
  'ュ': ['.....', '.....', '###..', '..#..', '..#..', '#####'],
  'ク': ['.#...', '.####', '#...#', '...#.', '..#..', '.#...'],
  'マ': ['#####', '....#', '.#.#.', '..#..', '...#.', '.....'],
  'ッ': ['.....', '.....', '#.#.#', '#.#.#', '...#.', '.##..'],
  // Hangul (8 wide): 고기 meat/BBQ, 카페 café, 마트 mart
  '고': ['#####...', '....#...', '....#...', '..#.....', '..#.....', '#######.'],
  '기': ['####.#..', '...#.#..', '...#.#..', '..#..#..', '.#...#..', '.....#..'],
  '카': ['####.#..', '...#.#..', '####.##.', '...#.#..', '..#..#..', '.#...#..'],
  '페': ['####.#.#', '.##..#.#', '.##.####', '.##..#.#', '####.#.#', '.....#.#'],
  '마': ['####.#..', '#..#.#..', '#..#.##.', '#..#.#..', '####.#..', '.....#..'],
  '트': ['#####...', '#.......', '#####...', '#.......', '#####...', '#######.'],
};
/** Katakana come from the pixel table; Latin letters and digits use a tiny bitmap-style font. */
export const glyphW = (ch: string) => (GLYPHS[ch]?.[0].length ?? 5);
export function glyph(g: G, ch: string, x: number, y: number, col: string) {
  if (GLYPHS[ch]) return stamp(g, GLYPHS[ch], x, y, { '#': col });
  g.fillStyle = col; g.font = 'bold 8px monospace'; g.textBaseline = 'top'; g.fillText(ch, x, y - 1);
}
/** Vertical neon board; vertical text turns the long-vowel bar upright. Returns its height. */
export function vSign(g: G, x: number, y: number, text: string, col: string, bg: string, lights: Lights) {
  const w = 11, h = [...text].length * 8 + 4;
  px(g, x, y, w, h, P.k); px(g, x + 1, y + 1, w - 2, h - 2, bg);
  g.strokeStyle = col; g.lineWidth = 1; g.strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);
  [...text].forEach((ch, i) => { const c = ch === 'ー' ? '｜' : ch; glyph(g, c, x + Math.floor((w - glyphW(c)) / 2), y + 3 + i * 8, P.white); });
  lights.push({ x, y, w, h, color: col, alpha: 0.55 });
  return h;
}
export const hSignW = (text: string) => [...text].reduce((s, c) => s + glyphW(c) + 2, 0) + 6;
/** A run of sign glyphs (katakana, hangul or Latin) without a board; returns its width. */
export function words(g: G, x: number, y: number, text: string, col: string) {
  let cx = x; for (const c of text) { glyph(g, c, cx, y, col); cx += glyphW(c) + 2; }
  return cx - x;
}
export function hSign(g: G, x: number, y: number, text: string, col: string, bg: string, lights: Lights) {
  const w = hSignW(text), h = 12;
  px(g, x, y, w, h, P.k); px(g, x + 1, y + 1, w - 2, h - 2, bg);
  g.strokeStyle = col; g.lineWidth = 1; g.strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);
  words(g, x + 4, y + 3, text, P.white);
  lights.push({ x, y, w, h, color: col, alpha: 0.6 });
  return w;
}

// ------------------------------------------------------------------ small sprites
const LANTERN = ['.kkkk.', 'krrrrk', 'kryrrk', 'krrrrk', 'krrrrk', 'kRRRRk', '.kkkk.'];
const LANTERN_S = ['.k.', 'rrr', 'ryr', 'rrr', '.R.'];
const MASK = ['..kkkkkkk..', '.kWWWWWWWk.', 'kWWWWWWWWWk', 'kWrrWWWrrWk', 'kWkrWWWrkWk', 'kWWWWWWWWWk', 'kWWWrWrWWWk', '.kWWWrWWWk.', '.kWWrrrWWk.', '..kWWWWWk..', '...kkkkk...'];
const INVADER = ['..#...#..', '...#.#...', '..#####..', '.##.#.##.', '#########', '#.#####.#', '#.#...#.#'];
export function lantern(g: G, x: number, y: number, col: string, dark: string, lights: Lights, small = false) {
  stamp(g, small ? LANTERN_S : LANTERN, x, y, { k: P.k, r: col, y: P.redLt, R: dark });
  const w = small ? 3 : 6, h = small ? 5 : 7;
  lights.push({ x, y, w, h, color: col, alpha: small ? 0.22 : 0.35 });
}
function vending(g: G, x: number, y: number, body: string, lights: Lights) {
  px(g, x, y, 9, 13, P.k); px(g, x + 1, y + 1, 7, 11, body); px(g, x + 1, y + 1, 7, 1, P.white);
  px(g, x + 2, y + 3, 5, 4, '#dff6ff');
  for (let i = 0; i < 5; i++) { px(g, x + 2 + i, y + 4, 1, 1, ['#e8433e', '#45e4f4', '#ffc56b', '#7fe07a', '#ff4fbc'][i]); px(g, x + 2 + i, y + 6, 1, 1, ['#45e4f4', '#ffc56b', '#e8433e', '#ff4fbc', '#7fe07a'][i]); }
  px(g, x + 2, y + 9, 5, 2, P.k);
  lights.push({ x, y: y + 2, w: 9, h: 6, color: '#bfefff', alpha: 0.2 });
}
function window1(g: G, x: number, y: number, glass: string, blind: string) {
  px(g, x, y, 10, 9, P.k); px(g, x + 1, y + 1, 8, 7, glass);
  px(g, x + 1, y + 1, 8, 1, blind); px(g, x + 1, y + 3, 8, 1, blind); px(g, x + 5, y + 1, 1, 7, P.k);
  px(g, x, y + 9, 10, 1, P.rimDk);
}

// ------------------------------------------------------------------ roofs
/** Extra roof materials and roof props registered by other painters (fronts-tokyo.ts); a roof painter gets its
 *  ROOFS palette. */
export const ROOF_PAINTERS: Record<string, (g: G, x: number, y: number, w: number, h: number, r: typeof ROOFS[string]) => void> = {};
export const PROP_PAINTERS: Record<string, (g: G, x: number, y: number, sh: string) => void> = {};
function roofSurface(g: G, x: number, y: number, w: number, h: number, kind: string) {
  const r = ROOFS[kind] ?? ROOFS.slate;
  px(g, x, y, w, h, r.base);
  if (ROOF_PAINTERS[kind]) ROOF_PAINTERS[kind](g, x, y, w, h, r);
  else if (kind === 'slate') {
    for (let j = 0; j < h; j += 24) for (let i = 0; i < w; i += 28) if (hash2(x + i, y + j) < 0.35) px(g, x + i, y + j, Math.min(28, w - i), Math.min(24, h - j), r.alt);
    for (let i = 28; i < w; i += 28) px(g, x + i, y, 1, h, r.seam);
    for (let j = 24; j < h; j += 24) px(g, x, y + j, w, 1, r.seam);
  } else if (kind === 'tar') {
    for (let j = 0; j < h; j += 2) for (let i = 0; i < w; i += 2) { const v = hash2(x + i, y + j); if (v < 0.07) px(g, x + i, y + j, 1, 1, r.alt); else if (v > 0.95) px(g, x + i, y + j, 1, 1, r.seam); }
    for (let k = 0; k < 3; k++) { const v = hash2(x + k * 13, y + k * 7); px(g, x + 8 + Math.floor(v * (w - 40)), y + 10 + Math.floor(hash2(k, x) * (h - 30)), 26, 14, r.alt); }
  } else if (kind === 'ribbed') {
    for (let i = 2; i < w; i += 5) { px(g, x + i, y, 1, h, r.alt); px(g, x + i + 1, y, 1, h, r.seam); }
  } else if (kind === 'tile') {
    for (let j = 0; j < h; j += 5) {
      px(g, x, y + j + 4, w, 1, r.seam);
      for (let i = (j / 5) % 2 ? 3 : 0; i < w; i += 6) { px(g, x + i, y + j, 1, 4, r.seam); px(g, x + i + 1, y + j, 3, 1, r.alt); }
    }
    px(g, x, y + Math.floor(h / 2) - 2, w, 4, r.alt); px(g, x, y + Math.floor(h / 2) + 2, w, 1, r.seam); // ridge cap
  }
}
/** Parapet: dark gap, lit rim on top/left, darker rim on the right, inner cast shadow below/right of the rim. */
function parapet(g: G, x: number, y: number, w: number, h: number, kind: string) {
  const sh = (ROOFS[kind] ?? ROOFS.slate).shade;
  px(g, x + 3, y + 3, w - 6, 2, sh); px(g, x + 3, y + 3, 2, h - 3, sh);
  px(g, x, y, w, 1, P.k); px(g, x, y, 1, h, P.k); px(g, x + w - 1, y, 1, h, P.k);
  px(g, x + 1, y + 1, w - 2, 2, P.rim); px(g, x + 1, y + 1, 2, h - 1, P.rim); px(g, x + w - 3, y + 1, 2, h - 1, P.rimDk);
}
function acUnit(g: G, x: number, y: number, sh: string) {
  px(g, x + 2, y + 2, 16, 13, sh); box(g, x, y, 16, 13, P.metal, P.metalDk, P.metalLt);
  disc(g, x + 7, y + 5, 4, P.k); disc(g, x + 7, y + 5, 3, P.metalDk);
  px(g, x + 4, y + 5, 7, 1, P.metal); px(g, x + 7, y + 2, 1, 7, P.metal); px(g, x + 7, y + 5, 1, 1, P.metalLt);
  for (let i = 0; i < 3; i++) px(g, x + 13, y + 2 + i * 3, 1, 2, P.metalDk);
}
function waterTank(g: G, x: number, y: number, sh: string) {
  disc(g, x + 13, y + 14, 10, sh);
  for (const [dx, dy] of [[3, 3], [17, 3], [3, 17], [17, 17]]) px(g, x + dx, y + dy, 2, 3, P.k);
  disc(g, x + 11, y + 11, 10, P.k); disc(g, x + 11, y + 11, 9, '#6c7a90'); disc(g, x + 11, y + 11, 7, '#7f8ea4');
  disc(g, x + 9, y + 9, 3, '#a9b5c6'); px(g, x + 10, y + 1, 2, 4, P.k); px(g, x + 9, y + 10, 5, 3, P.k); px(g, x + 10, y + 11, 3, 1, P.metalDk);
}
function stairHut(g: G, x: number, y: number, sh: string) {
  px(g, x + 3, y + 3, 26, 24, sh);
  box(g, x, y, 26, 18, '#55647a', '#3c475b', P.rim);
  px(g, x, y + 16, 26, 8, P.k); px(g, x + 1, y + 16, 24, 7, '#2a3446'); // south wall, taller than a box edge
  px(g, x + 10, y + 17, 7, 7, P.k); px(g, x + 11, y + 18, 5, 6, '#3a2a20'); px(g, x + 15, y + 20, 1, 1, P.amber);
  px(g, x + 12, y + 16, 3, 1, P.amberLt); px(g, x + 3, y + 5, 20, 1, '#46546a');
}
function vent(g: G, x: number, y: number, sh: string) {
  px(g, x + 2, y + 2, 10, 8, sh); box(g, x, y, 10, 8, P.metalDk, '#2c3646', P.metal);
  for (let i = 0; i < 3; i++) px(g, x + 2, y + 2 + i * 2, 6, 1, P.k);
}
function dish(g: G, x: number, y: number, sh: string) {
  disc(g, x + 7, y + 7, 5, sh); disc(g, x + 5, y + 5, 5, P.k); disc(g, x + 5, y + 5, 4, '#c3ccd8'); disc(g, x + 4, y + 4, 2, P.white);
  px(g, x + 5, y + 5, 5, 1, P.metalDk); px(g, x + 9, y + 4, 2, 2, P.k);
}
function skylight(g: G, x: number, y: number, sh: string) {
  px(g, x + 2, y + 2, 24, 14, sh); box(g, x, y, 24, 14, '#1d3146', '#2a3446', P.metalLt);
  for (let i = 4; i < 22; i += 6) px(g, x + i, y + 2, 1, 9, '#3f6c88');
  px(g, x + 2, y + 3, 20, 1, '#2d4c66');
}
/** Pipe run: horizontal to (x2, y1), then vertical to (x2, y2). */
function duct(g: G, x1: number, y1: number, x2: number, y2: number, sh: string) {
  const seg = (x: number, y: number, w: number, h: number) => {
    px(g, x + 2, y + 2, w, h, sh); px(g, x, y, w, h, P.k);
    if (w > h) { px(g, x, y + 1, w, 2, P.metal); px(g, x, y + 1, w, 1, P.metalLt); }
    else { px(g, x + 1, y, 2, h, P.metal); px(g, x + 1, y, 1, h, P.metalLt); }
  };
  const lx = Math.min(x1, x2), rx = Math.max(x1, x2);
  seg(lx, y1, rx - lx + 4, 4);
  const ty = Math.min(y1, y2), by = Math.max(y1, y2);
  if (by > ty) seg(x2, ty, 4, by - ty + 4);
  px(g, x2 - 1, y1 - 1, 6, 6, P.k); px(g, x2, y1, 4, 4, P.metalDk); px(g, x2, y1, 4, 1, P.metalLt);
}
/** Kitchen exhaust hood: a boxy fan housing, a cap, a soot smear and a little steam. */
function hood(g: G, x: number, y: number, sh: string) {
  px(g, x + 2, y + 2, 16, 14, sh);
  px(g, x + 14, y + 4, 8, 10, '#22252c'); px(g, x + 15, y + 5, 7, 8, '#2c2f36'); // soot
  box(g, x, y, 16, 14, '#5c6676', '#3a4250', P.metalLt);
  px(g, x + 4, y + 3, 8, 6, P.k); px(g, x + 5, y + 4, 6, 4, P.metalDk); px(g, x + 5, y + 5, 6, 1, P.metal);
  for (let i = 0; i < 3; i++) px(g, x + 6 + i * 2, y - 3 - (i % 2) * 2, 1, 2, '#c8d0dc');
}
function roofProp(g: G, p: (string | number)[], ox: number, oy: number, sh: string) {
  const [k, a, b, c, d] = p as [string, number, number, number?, number?];
  const x = ox + a, y = oy + b;
  if (k === 'ac') acUnit(g, x, y, sh);
  else if (k === 'tank') waterTank(g, x, y, sh);
  else if (k === 'hut') stairHut(g, x, y, sh);
  else if (k === 'vent') vent(g, x, y, sh);
  else if (k === 'dish') dish(g, x, y, sh);
  else if (k === 'skylight') skylight(g, x, y, sh);
  else if (k === 'hood') hood(g, x, y, sh);
  else if (k === 'duct') duct(g, x, y, ox + (c ?? 0), oy + (d ?? 0), sh);
  else PROP_PAINTERS[k]?.(g, x, y, sh);
}
/** A stepped roof: the tall rear section's south wall (with windows) is visible above the lower front roof. */
function paintLotRoof(g: G, x: number, y: number, w: number, h: number, lot: Lot) {
  const kind = lot.roof, sh = (ROOFS[kind] ?? ROOFS.slate).shade;
  roofSurface(g, x, y, w, h, kind);
  if (lot.step) {
    const sy = y + lot.step * TILE, lowKind = lot.lowRoof ?? kind;
    roofSurface(g, x, sy, w, y + h - sy, lowKind);
    parapet(g, x, sy, w, y + h - sy, lowKind);
    px(g, x + 1, sy + 3, w - 2, 6, P.shade); // shadow the tall section casts onto the low roof
    px(g, x, sy - 10, w, 1, P.k); px(g, x + 1, sy - 9, w - 2, 10, P.wall); px(g, x + 1, sy - 9, w - 2, 1, P.rimDk);
    for (let i = x + 6; i + 6 < x + w - 4; i += 11) { const lit = hash2(i, sy) < 0.4; px(g, i, sy - 6, 6, 4, P.k); px(g, i + 1, sy - 5, 4, 2, lit ? P.amber : '#24304a'); }
  }
  parapet(g, x, y, w, lot.step ? lot.step * TILE - 9 : h, kind);
  for (const p of lot.props) roofProp(g, p, x, y, sh);
}

// ------------------------------------------------------------------ façades
export function spill(g: G, m: GameMap, x: number, y: number, w: number, col: string, strength: number) {
  for (let band = 0; band < 3; band++) {
    g.fillStyle = col; g.globalAlpha = strength * (0.5 - band * 0.15);
    const inset = band * 4, yy = y + band * 5;
    for (let i = x + inset; i < x + w - inset; i += 2) {
      const t = tileAt(m, Math.floor(i / TILE), Math.floor(yy / TILE));
      if (t === T.SIDEWALK || t === T.ROAD || t === T.PLAZA || t === T.ALLEY) g.fillRect(i, yy, 2, 5);
    }
  }
  g.globalAlpha = 1;
}
function baseFacade(g: G, x: number, y: number, w: number, F: number, wall: string, glass: string, blind: string) {
  px(g, x, y, w, F, wall);
  px(g, x, y, w, 1, P.k); px(g, x, y + 1, w, 2, P.rimDk); px(g, x, y + 3, w, 1, P.wallDk);
  for (let wx = x + 5; wx + 10 <= x + w - 4; wx += 14) window1(g, wx, y + 6, hash2(wx, y) < 0.45 ? glass : '#1b2438', blind);
  px(g, x, y, 1, F, P.k); px(g, x + w - 1, y, 1, F, P.k); px(g, x, y + F - 2, w, 2, P.k);
}
function arcade(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  baseFacade(g, x, y, w, F, '#22183a', '#5b3c9e', '#3a2870');
  const sx = x + Math.floor((w - hSignW('ゲーム')) / 2) + 6;
  hSign(g, sx, y + 6, 'ゲーム', P.magenta, '#2a0a26', L);
  stamp(g, INVADER, sx - 13, y + 8, { '#': P.cyan }); L.push({ x: sx - 13, y: y + 8, w: 9, h: 7, color: P.cyan, alpha: 0.4 });
  px(g, x + 1, y + 21, w - 2, 1, P.magenta); px(g, x + 1, y + 22, w - 2, 1, P.pink); L.push({ x, y: y + 20, w, h: 3, color: P.magenta, alpha: 0.35 });
  // glass front full of cabinets
  px(g, x + 3, y + 25, w - 6, 19, P.k); px(g, x + 4, y + 26, w - 8, 17, '#2b1450');
  for (let cx = x + 6; cx + 5 < x + w - 6; cx += 7) {
    px(g, cx, y + 29, 5, 9, '#120a22'); px(g, cx + 1, y + 30, 3, 3, ['#45e4f4', '#ff4fbc', '#ffe03a', '#9466ee'][(cx >> 3) % 4]);
    px(g, cx + 1, y + 34, 3, 1, '#5a4a7a');
  }
  const dx = x + Math.floor(w / 2) - 9;
  px(g, dx, y + 24, 18, 22, P.magenta); px(g, dx + 1, y + 25, 16, 21, '#ff9ad8'); px(g, dx + 8, y + 25, 1, 21, P.magentaDk);
  px(g, dx + 2, y + 38, 14, 8, '#ffd6f0');
  for (let i = 0; i < 3; i++) { const cx = x + w - 20 + i * 6; px(g, cx, y + 37, 5, 8, P.k); disc(g, cx + 2, y + 39, 1, ['#45e4f4', '#ffe03a', '#ff4fbc'][i]); px(g, cx + 1, y + 42, 3, 2, '#3a2a50'); }
  L.push({ x: x + 4, y: y + 26, w: w - 8, h: 17, color: P.violet, alpha: 0.28 }, { x: dx, y: y + 24, w: 18, h: 22, color: P.magenta, alpha: 0.5 });
  spill(g, m, dx - 18, y + F, 54, P.magenta, 0.55); spill(g, m, x + 4, y + F, w - 8, P.violet, 0.25);
}
function karaoke(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  baseFacade(g, x, y, w, F, '#14283a', '#1f6b80', '#2a8ea0');
  for (let wx = x + 5, i = 0; wx + 10 <= x + w - 18; wx += 14, i++) { // karaoke booths: colored windows with a silhouette
    const c = [P.cyan, P.magenta, P.violet][i % 3]; px(g, wx + 1, y + 7, 8, 7, c); px(g, wx + 3, y + 9, 2, 5, P.k); px(g, wx + 2, y + 8, 4, 2, P.k);
    L.push({ x: wx, y: y + 6, w: 10, h: 9, color: c, alpha: 0.22 });
  }
  // striped teal awning with a scalloped edge
  for (let i = 0; i < w - 2; i++) px(g, x + 1 + i, y + 19, 1, 6, Math.floor(i / 4) % 2 ? P.cyanDk : P.cyan);
  for (let i = 0; i < w - 2; i += 4) px(g, x + 1 + i, y + 25, 2, 1, P.cyanDk);
  px(g, x + 1, y + 19, w - 2, 1, P.cyanLt);
  px(g, x + 4, y + 28, w - 8, 15, P.k); px(g, x + 5, y + 29, w - 10, 13, '#10303c');
  for (let i = x + 8; i < x + w - 24; i += 16) { px(g, i, y + 31, 9, 10, '#1d4a5a'); px(g, i + 1, y + 32, 7, 4, ['#ff4fbc', '#ffe03a', '#45e4f4'][(i >> 4) % 3]); }
  const dx = x + w - 40;
  px(g, dx, y + 26, 16, 20, P.cyan); px(g, dx + 1, y + 27, 14, 19, '#9ff6ff'); px(g, dx + 7, y + 27, 1, 19, P.cyanDk); px(g, dx + 2, y + 39, 12, 7, P.cyanLt);
  L.push({ x: dx, y: y + 26, w: 16, h: 20, color: P.cyan, alpha: 0.55 });
  vending(g, dx - 22, y + F - 15, '#c8323c', L); vending(g, dx - 12, y + F - 15, '#2a6ad0', L);
  // tall vertical sign hanging over the roof edge by the alley corner
  vSign(g, x + w - 15, y - 26, 'カラオケ', P.cyan, '#05242c', L);
  spill(g, m, dx - 16, y + F, 48, P.cyan, 0.6);
}
function kabuki(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  baseFacade(g, x, y, w, F, '#2a0d1c', '#8e2450', '#5a1838');
  const gx = x + 14, gw = w - 28;
  // the gate: lit passage, posts, a beam with neon underside, lanterns and the mask over the beam
  px(g, gx, y + 12, gw, F - 14, '#5a1440');
  for (let j = 0; j < 4; j++) px(g, gx + 8, y + 22 + j * 6, gw - 16, 6, ['#7a1c56', '#a52a72', '#d0418e', '#ff6fc4'][j]);
  px(g, gx + 20, y + 22, gw - 40, 8, '#2a0d1c'); px(g, gx + 22, y + 23, gw - 44, 7, '#ffd0ea');
  for (const ppx of [gx, gx + gw - 8]) { px(g, ppx, y + 8, 8, F - 10, P.k); px(g, ppx + 1, y + 9, 6, F - 12, P.redDk); px(g, ppx + 1, y + 9, 2, F - 12, P.red); }
  px(g, gx - 4, y + 6, gw + 8, 8, P.k); px(g, gx - 3, y + 7, gw + 6, 6, '#3a1018'); px(g, gx - 3, y + 7, gw + 6, 1, P.red);
  px(g, gx + 8, y + 14, gw - 16, 1, P.magenta); L.push({ x: gx, y: y + 12, w: gw, h: 4, color: P.magenta, alpha: 0.45 });
  for (let i = 0; i < 6; i++) lantern(g, gx + 10 + i * Math.floor((gw - 26) / 5), y + 16, P.red, P.redDk, L);
  stamp(g, MASK, x + Math.floor(w / 2) - 5, y - 6, { k: P.k, W: P.white, r: P.red });
  L.push({ x: x + Math.floor(w / 2) - 5, y: y - 6, w: 11, h: 11, color: '#ffe0f0', alpha: 0.3 }, { x: gx + 8, y: y + 22, w: gw - 16, h: 24, color: P.magenta, alpha: 0.5 });
  vSign(g, x + 2, y - 20, 'カブキ', P.magenta, '#2a0420', L);
  spill(g, m, gx, y + F, gw, P.magenta, 0.9);
}
function izakaya(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  px(g, x, y, w, F, P.wood); for (let i = x + 3; i < x + w; i += 4) px(g, i, y, 1, F, P.woodDk);
  px(g, x, y, w, 1, P.k); px(g, x, y + 1, w, 2, P.woodLt); px(g, x, y, 1, F, P.k); px(g, x + w - 1, y, 1, F, P.k);
  for (let wx = x + 6; wx + 18 <= x + w - 6; wx += 24) { // shoji windows glowing amber
    px(g, wx, y + 5, 18, 11, P.k); px(g, wx + 1, y + 6, 16, 9, P.amberLt);
    for (let i = 4; i < 16; i += 4) px(g, wx + 1 + i, y + 6, 1, 9, P.amberDk);
    px(g, wx + 1, y + 10, 16, 1, P.amberDk); L.push({ x: wx, y: y + 5, w: 18, h: 11, color: P.amber, alpha: 0.25 });
  }
  // tiled eave with a scalloped edge (Lantern & Neon)
  px(g, x, y + 18, w, 8, P.k);
  for (let i = 0; i < w; i += 4) { px(g, x + i, y + 19, 3, 6, '#4b5262'); px(g, x + i, y + 19, 1, 6, '#646c7e'); px(g, x + i + 1, y + 25, 2, 1, '#2a2e38'); }
  const dx = x + Math.floor(w / 2) - 16;
  px(g, dx, y + 27, 32, 19, P.k); px(g, dx + 1, y + 28, 30, 18, '#ffcf86'); px(g, dx + 1, y + 38, 30, 8, P.amberLt);
  for (let i = 0; i < 3; i++) { px(g, dx + 1 + i * 10, y + 27, 9, 10, P.navy); px(g, dx + 1 + i * 10, y + 27, 9, 1, '#34468a'); }
  disc(g, dx + 15, y + 31, 2, P.white);
  for (const lx of [dx - 9, dx + 35]) lantern(g, lx, y + 28, P.red, P.redDk, L);
  // lattice window, crates and a chalk menu board
  const wx = x + w - 34; px(g, wx, y + 29, 28, 14, P.k); px(g, wx + 1, y + 30, 26, 12, '#ffb85a');
  for (let i = 3; i < 26; i += 4) px(g, wx + 1 + i, y + 30, 1, 12, P.woodDk); px(g, wx + 1, y + 35, 26, 1, P.woodDk);
  if (w >= 112) { // room for crates and a chalk menu beside the door
    for (let i = 0; i < 3; i++) { px(g, x + 6 + i * 7, y + 38 - (i === 1 ? 6 : 0), 7, 6, P.k); px(g, x + 7 + i * 7, y + 39 - (i === 1 ? 6 : 0), 5, 4, i === 1 ? '#c8a020' : '#b0302c'); }
    px(g, x + 30, y + 30, 10, 14, P.woodDk); px(g, x + 31, y + 31, 8, 9, '#1a1f1c'); for (let i = 0; i < 4; i++) px(g, x + 32, y + 32 + i * 2, 4 + (i % 2) * 2, 1, '#d8e0d0');
    L.push({ x: wx, y: y + 29, w: 28, h: 14, color: P.amber, alpha: 0.3 });
  } else px(g, x + w - 35, y + 27, 34, 19, P.wood); // narrow lot: plain boards instead of the lattice window
  px(g, x, y + F - 2, w, 2, P.k);
  L.push({ x: dx, y: y + 27, w: 32, h: 19, color: P.amber, alpha: 0.45 });
  spill(g, m, dx - 10, y + F, 52, P.amber, 0.55);
}
/** Plain ground-floor frame shared by the simpler fronts: dark band, door, step. */
export function doorway(g: G, x: number, y: number, w: number, h: number, frame: string, inside: string) {
  px(g, x, y, w, h, P.k); px(g, x + 1, y + 1, w - 2, h - 1, frame); px(g, x + 2, y + 2, w - 4, h - 2, inside);
  px(g, x + Math.floor(w / 2), y + 2, 1, h - 2, frame);
}
// --- Midnight Concrete (01): quiet city fronts
function office(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  baseFacade(g, x, y, w, F, '#1c2a3e', '#a9c9de', '#7fa3bd');
  for (let wx = x + 5; wx + 10 <= x + w - 4; wx += 14) window1(g, wx, y + 6, hash2(wx, y + 1) < 0.5 ? '#a9c9de' : '#1b2438', '#7fa3bd');
  px(g, x + 1, y + 19, w - 2, 3, '#2f4a5c'); px(g, x + 1, y + 19, w - 2, 1, '#8fb7c6');
  px(g, x + 3, y + 24, w - 6, 21, P.k); px(g, x + 4, y + 25, w - 8, 20, '#163245');
  for (let i = x + 10; i < x + w - 6; i += 12) px(g, i, y + 25, 1, 20, '#2b5068');
  const dx = x + Math.floor(w / 2) - 8; doorway(g, dx, y + 28, 16, 18, '#8fb7c6', '#cfeaf5');
  px(g, x + 6, y + 30, 8, 4, '#c8d6e0'); // name plate
  L.push({ x: dx, y: y + 28, w: 16, h: 18, color: '#cfeaf5', alpha: 0.4 }, { x: x + 4, y: y + 25, w: w - 8, h: 20, color: '#7fc8e0', alpha: 0.15 });
  spill(g, m, dx - 10, y + F, 36, '#cfeaf5', 0.35);
}
function konbini(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  baseFacade(g, x, y, w, F, '#1e2838', '#d8e6f0', '#9fb3c4');
  // signature stripes over a bright glass front
  px(g, x + 1, y + 17, w - 2, 7, P.white); px(g, x + 1, y + 19, w - 2, 1, '#2f9a5a'); px(g, x + 1, y + 21, w - 2, 1, '#2a6ad0');
  glyph(g, '2', x + 4, y + 18, '#2a6ad0'); glyph(g, '4', x + 9, y + 18, '#2a6ad0'); glyph(g, 'H', x + 14, y + 18, '#e8433e');
  px(g, x + 3, y + 25, w - 6, 20, P.k); px(g, x + 4, y + 26, w - 8, 19, '#eef8ff');
  for (let ry = y + 28; ry < y + 42; ry += 5) for (let i = x + 6; i < x + w - 26; i += 2) px(g, i, ry, 1, 2, ['#e8433e', '#2a6ad0', '#ffc56b', '#7fe07a'][(i + ry) % 4]);
  doorway(g, x + w - 22, y + 27, 16, 19, '#9fb3c4', '#ffffff');
  px(g, x + 2, y + 40, 6, 6, P.k); px(g, x + 3, y + 41, 4, 4, '#3a4a5a'); // bins
  L.push({ x: x + 4, y: y + 17, w: w - 8, h: 28, color: '#e8f6ff', alpha: 0.45 });
  spill(g, m, x + 2, y + F, w - 4, '#e8f6ff', 0.45);
}
function apartment(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  px(g, x, y, w, F, '#283246'); px(g, x, y, w, 1, P.k); px(g, x, y + 1, w, 2, P.rimDk); px(g, x, y, 1, F, P.k); px(g, x + w - 1, y, 1, F, P.k);
  for (let fl = 0; fl < 2; fl++) for (let wx = x + 4; wx + 12 <= x + w - 3; wx += 15) { // balconies with railings and laundry
    const by = y + 5 + fl * 13, lit = hash2(wx, by) < 0.3;
    px(g, wx, by, 12, 10, P.k); px(g, wx + 1, by + 1, 10, 5, lit ? '#e8b46a' : '#1a2234');
    px(g, wx, by + 6, 12, 1, '#8d9bb1'); for (let i = 1; i < 12; i += 2) px(g, wx + i, by + 7, 1, 3, '#56637a');
    if (hash2(wx, by + 5) < 0.4) px(g, wx + 3, by + 3, 4, 3, ['#e8e8f0', '#ff9a84', '#7fc8e0'][Math.floor(hash2(by, wx) * 3)]);
    if (lit) L.push({ x: wx, y: by, w: 12, h: 6, color: P.amber, alpha: 0.18 });
  }
  const dx = x + 6; doorway(g, dx, y + 32, 14, 14, '#56637a', '#c9a868');
  for (let i = 0; i < 4; i++) px(g, dx + 18 + i * 3, y + 34, 2, 3, '#8d9bb1'); // mailboxes
  px(g, x + w - 22, y + 39, 14, 1, P.metalLt); disc(g, x + w - 20, y + 42, 2, P.k); disc(g, x + w - 10, y + 42, 2, P.k); // bicycle
  px(g, x, y + F - 2, w, 2, P.k);
  L.push({ x: dx, y: y + 32, w: 14, h: 14, color: P.amber, alpha: 0.3 });
  spill(g, m, dx - 6, y + F, 26, P.amber, 0.3);
}
function shutter(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  baseFacade(g, x, y, w, F, '#232a36', '#55617a', '#3a4458');
  px(g, x + 3, y + 22, w - 6, 24, P.k);
  for (let j = 0; j < 22; j += 2) { px(g, x + 4, y + 23 + j, w - 8, 1, '#6b778c'); px(g, x + 4, y + 24 + j, w - 8, 1, '#4b5568'); }
  for (let k = 0; k < 3; k++) { // graffiti tags
    const gx = x + 8 + Math.floor(hash2(x, k) * (w - 30)), col = ['#ff4fbc', '#45e4f4', '#d8ff3a'][k];
    for (let i = 0; i < 12; i++) px(g, gx + i, y + 30 + Math.round(Math.sin((i + k) * 1.3) * 2), 1, 2, col);
  }
  px(g, x + Math.floor(w / 2) - 2, y + 19, 5, 2, P.amberLt); L.push({ x: x + Math.floor(w / 2) - 3, y: y + 18, w: 7, h: 3, color: P.amber, alpha: 0.3 });
}
function clinic(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  baseFacade(g, x, y, w, F, '#2c3a4a', '#bfeee8', '#7fbfb8');
  px(g, x + 1, y + 18, w - 2, 5, '#d8e2ea'); px(g, x + 1, y + 22, w - 2, 1, '#8fa4b4');
  const cx = x + Math.floor(w / 2); // teal neon cross over the entrance
  px(g, cx - 7, y + 2, 15, 15, P.k); px(g, cx - 6, y + 3, 13, 13, '#0a2a2a');
  px(g, cx - 1, y + 4, 3, 11, P.cyan); px(g, cx - 5, y + 8, 11, 3, P.cyan); px(g, cx, y + 5, 1, 9, P.cyanLt);
  px(g, x + 3, y + 24, w - 6, 21, P.k); px(g, x + 4, y + 25, w - 8, 20, '#123a3a');
  for (let i = x + 8; i < x + w - 8; i += 10) px(g, i, y + 27, 6, 10, '#2a6a66');
  doorway(g, cx - 10, y + 26, 20, 20, P.cyan, '#d6fbff');
  L.push({ x: cx - 7, y: y + 2, w: 15, h: 15, color: P.cyan, alpha: 0.6 }, { x: cx - 10, y: y + 26, w: 20, h: 20, color: P.cyanLt, alpha: 0.45 });
  spill(g, m, cx - 20, y + F, 40, P.cyan, 0.5);
}
// --- Lantern & Neon (03): warm old shops
function ramen(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  px(g, x, y, w, F, P.woodDk); for (let i = x + 2; i < x + w; i += 5) px(g, i, y, 1, F, P.wood);
  px(g, x, y, w, 1, P.k); px(g, x, y + 1, w, 2, P.woodLt); px(g, x, y, 1, F, P.k); px(g, x + w - 1, y, 1, F, P.k);
  const sx = x + Math.floor((w - hSignW('ラーメン')) / 2); hSign(g, sx, y + 5, 'ラーメン', P.red, '#5a1018', L);
  for (let i = 0; i < 4; i++) px(g, x + 8 + i * 3, y + 6 - (i % 2), 1, 3, '#d8dde6'); // steam
  const dx = x + Math.floor(w / 2) - 14;
  px(g, dx, y + 21, 28, 25, P.k); px(g, dx + 1, y + 22, 26, 24, '#ffd08a'); px(g, dx + 1, y + 38, 26, 2, P.woodDk);
  for (let i = 0; i < 3; i++) { px(g, dx + 1 + i * 9, y + 21, 8, 11, '#b8282c'); disc(g, dx + 5 + i * 9, y + 26, 1, P.white); }
  for (let i = 0; i < 4; i++) px(g, dx + 3 + i * 6, y + 41, 3, 4, P.k); // stools
  for (const lx of [dx - 8, dx + 30]) if (lx > x + 1 && lx + 6 < x + w - 1) lantern(g, lx, y + 23, P.red, P.redDk, L);
  px(g, x, y + F - 2, w, 2, P.k);
  L.push({ x: dx, y: y + 21, w: 28, h: 25, color: P.amber, alpha: 0.45 });
  spill(g, m, dx - 8, y + F, 44, P.amber, 0.5);
}
function shop(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  px(g, x, y, w, F, '#3b2a22'); px(g, x, y, w, 1, P.k); px(g, x, y + 1, w, 2, P.woodLt); px(g, x, y, 1, F, P.k); px(g, x + w - 1, y, 1, F, P.k);
  for (let wx = x + 5; wx + 12 <= x + w - 4; wx += 16) { px(g, wx, y + 6, 12, 9, P.k); px(g, wx + 1, y + 7, 10, 7, '#f0d9a8'); px(g, wx + 6, y + 7, 1, 7, P.woodDk); px(g, wx + 1, y + 10, 10, 1, P.woodDk); }
  px(g, x, y + 18, w, 5, '#2a3a4a'); for (let i = 0; i < w; i += 4) px(g, x + i, y + 22, 3, 1, '#16202a'); // short eave
  const dx = x + 8;
  px(g, dx, y + 25, 22, 21, P.k); px(g, dx + 1, y + 26, 20, 20, '#ffe2a8');
  for (let i = 0; i < 2; i++) px(g, dx + 1 + i * 10, y + 25, 10, 8, '#2a6a6a'); // teal noren
  for (let i = dx + 26; i + 8 < x + w - 4; i += 12) { px(g, i, y + 38, 8, 8, P.k); px(g, i + 1, y + 39, 6, 6, '#6a3a2a'); px(g, i + 1, y + 34, 6, 5, '#3f8a4a'); px(g, i + 2, y + 33, 2, 2, '#5fb86a'); } // potted plants
  stamp(g, ['.kk.', 'kWWk', 'krrk', 'kWWk', '.kk.'], x + w - 9, y + 24, { k: P.k, W: '#fff4e0', r: P.red }); // paper lantern
  px(g, x, y + F - 2, w, 2, P.k);
  L.push({ x: dx, y: y + 25, w: 22, h: 21, color: P.amber, alpha: 0.4 }, { x: x + w - 9, y: y + 24, w: 4, h: 5, color: '#fff4e0', alpha: 0.3 });
  spill(g, m, dx - 6, y + F, 34, P.amber, 0.4);
}
// --- Kabukicho Afterglow (02): nightlife fronts
function hotel(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  baseFacade(g, x, y, w, F, '#241a3a', '#e8a0c8', '#8a4a78');
  px(g, x + 1, y + 20, w - 2, 4, '#3a2458'); px(g, x + 1, y + 20, w - 2, 1, P.violet);
  const dx = x + Math.floor(w / 2) - 10;
  px(g, x + 3, y + 25, w - 6, 20, '#1a1230');
  for (let i = x + 5; i < x + w - 5; i += 6) px(g, i, y + 27, 4, 16, '#3a2a5a'); // frosted panels
  doorway(g, dx, y + 26, 20, 20, P.violet, '#ffc2ea');
  for (const px0 of [dx - 8, dx + 22]) { px(g, px0, y + 38, 6, 8, P.k); px(g, px0 + 1, y + 39, 4, 6, '#5a3a2a'); px(g, px0, y + 33, 6, 6, '#3f8a4a'); }
  vSign(g, x + w - 14, y - 22, 'ホテル', P.magenta, '#2a0420', L);
  L.push({ x: dx, y: y + 26, w: 20, h: 20, color: P.magenta, alpha: 0.5 });
  spill(g, m, dx - 8, y + F, 36, P.magenta, 0.5);
}
function bar(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  baseFacade(g, x, y, w, F, '#181428', '#5a2a6a', '#3a1a4a');
  hSign(g, x + 6, y + 21, 'バー', P.magenta, '#2a0420', L);
  for (let i = 0; i < 14; i++) px(g, x + w - 26 + i, y + 24 + Math.round(Math.sin(i * 0.9) * 2), 1, 1, P.cyan); // neon script
  L.push({ x: x + w - 27, y: y + 21, w: 16, h: 6, color: P.cyan, alpha: 0.45 });
  const dx = x + Math.floor(w / 2) - 6; doorway(g, dx, y + 30, 13, 16, '#3a2a40', '#2a1a30');
  disc(g, dx + 6, y + 36, 2, P.pink); L.push({ x: dx + 3, y: y + 33, w: 7, h: 6, color: P.magenta, alpha: 0.4 });
  px(g, x, y + F - 2, w, 2, P.k);
  spill(g, m, dx - 6, y + F, 26, P.magenta, 0.35);
}
function pachinko(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  baseFacade(g, x, y, w, F, '#2a1e10', '#c88a2a', '#7a5018');
  px(g, x + 1, y + 4, w - 2, 15, '#3a2408'); // bulb-bordered sign board
  for (let i = x + 2; i < x + w - 2; i += 2) { px(g, i, y + 4, 1, 1, (i >> 1) % 2 ? P.amberLt : P.amber); px(g, i, y + 18, 1, 1, (i >> 1) % 2 ? P.amber : P.amberLt); }
  hSign(g, x + Math.floor((w - hSignW('パチンコ')) / 2), y + 6, 'パチンコ', P.amber, '#3a1a08', L);
  px(g, x + 3, y + 22, w - 6, 23, P.k); px(g, x + 4, y + 23, w - 8, 22, '#3a1a2a');
  for (let i = x + 6; i < x + w - 6; i += 5) { px(g, i, y + 25, 3, 12, ['#ff4fbc', '#45e4f4', '#ffc56b', '#9466ee'][(i >> 2) % 4]); px(g, i, y + 38, 3, 2, '#c0a060'); }
  const dx = x + Math.floor(w / 2) - 10; doorway(g, dx, y + 25, 20, 21, P.amber, '#fff1c4');
  L.push({ x: x + 1, y: y + 4, w: w - 2, h: 15, color: P.amber, alpha: 0.5 }, { x: dx, y: y + 25, w: 20, h: 21, color: P.amberLt, alpha: 0.45 });
  spill(g, m, x + 4, y + F, w - 8, P.amber, 0.6);
}
// --- Environment-art test scene (Hotel Corner crossroads): denser fronts with awnings, utilities and wet glints
/** Striped fabric awning: lit top edge, scalloped hem and a 1 px shadow on the wall below. */
export function awning(g: G, x: number, y: number, w: number, a: string, b: string, lit: string) {
  px(g, x, y, w, 8, P.k);
  for (let i = 0; i < w - 2; i++) px(g, x + 1 + i, y + 1, 1, 6, Math.floor(i / 5) % 2 ? b : a);
  px(g, x + 1, y + 1, w - 2, 1, lit);
  for (let i = 0; i < w - 2; i += 5) px(g, x + 1 + i, y + 7, 3, 1, Math.floor(i / 5) % 2 ? b : a);
  px(g, x + 1, y + 8, w - 2, 1, '#0b0f1a');
}
/** Wall-mounted AC unit with a drip pipe running down the wall. */
export function wallAc(g: G, x: number, y: number) {
  px(g, x, y, 13, 9, P.k); px(g, x + 1, y + 1, 11, 7, P.metal); px(g, x + 1, y + 1, 11, 1, P.metalLt);
  for (let i = 0; i < 3; i++) px(g, x + 2, y + 3 + i * 2, 6, 1, P.metalDk);
  disc(g, x + 9, y + 4, 1, P.metalDk); px(g, x + 11, y + 9, 1, 6, P.metalDk);
}
/** Electric meter box with a cable leading up the wall. */
export function meter(g: G, x: number, y: number) {
  px(g, x + 2, y - 8, 1, 8, '#05070d'); px(g, x, y, 6, 8, P.k); px(g, x + 1, y + 1, 4, 6, '#8d9bb1');
  px(g, x + 2, y + 2, 2, 2, '#e8f0f8'); px(g, x + 1, y + 6, 4, 1, P.metalDk);
}
/** Noren: split cloth panels over a doorway with one white crest. */
export function noren(g: G, x: number, y: number, w: number, cloth: string, hem: string) {
  const n = Math.max(2, Math.floor(w / 9)), pw = Math.floor(w / n);
  for (let i = 0; i < n; i++) { px(g, x + i * pw, y, pw - 1, 10, cloth); px(g, x + i * pw, y + 9, pw - 1, 1, hem); }
  disc(g, x + Math.floor(w / 2), y + 4, 2, P.white);
}
function menuBoard(g: G, x: number, y: number, L: Lights) {
  px(g, x, y, 13, 15, P.k); px(g, x + 1, y + 1, 11, 13, '#fff1d6');
  for (let j = 0; j < 3; j++) for (let i = 0; i < 2; i++) px(g, x + 2 + i * 5, y + 2 + j * 4, 4, 3, ['#c8642c', '#e8b44a', '#b8282c'][(i + j) % 3]);
  px(g, x + 5, y + 15, 3, 2, P.k);
  L.push({ x, y, w: 13, h: 15, color: P.amberLt, alpha: 0.3 });
}
export function aFrame(g: G, x: number, y: number, face: string, ink: string) {
  px(g, x, y, 9, 10, P.k); px(g, x + 1, y + 1, 7, 8, face);
  for (let j = 0; j < 3; j++) px(g, x + 2, y + 2 + j * 2, 3 + (j % 2) * 2, 1, ink);
}
export function plant(g: G, x: number, y: number) {
  px(g, x, y + 5, 7, 6, P.k); px(g, x + 1, y + 6, 5, 4, '#5a3a2a');
  px(g, x, y, 7, 6, '#2f6a3a'); px(g, x + 1, y + 1, 2, 2, '#5fb86a'); px(g, x + 4, y + 2, 2, 2, '#4a9a52');
}
/** A few short wet-pavement glints under a light, only on walkable ground. Static, so they stay in Low effects. */
function wetGlint(g: G, m: GameMap, x: number, y: number, w: number, col: string, a: number) {
  g.fillStyle = col;
  for (let k = 0; k < Math.floor(w / 5); k++) {
    const gx = x + Math.floor(hash2(x + k, y) * w), gy = y + 2 + Math.floor(hash2(y, x + k) * 14), gl = 3 + Math.floor(hash2(k, gx) * 5);
    const t = tileAt(m, Math.floor(gx / TILE), Math.floor(gy / TILE));
    if (t !== T.SIDEWALK && t !== T.ROAD && t !== T.PLAZA) continue;
    g.globalAlpha = Math.min(1, a * 1.4 * (1 - (gy - y) / 20)); g.fillRect(gx, gy, gl, 1);
  }
  g.globalAlpha = 1;
}
function ramenHouse(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  // upper floor: dark plank wall, shoji windows, the shop's name board, a wall AC and a sagging cable
  px(g, x, y, w, F, P.woodDk); for (let i = x + 2; i < x + w; i += 5) px(g, i, y + 3, 1, 15, P.wood);
  px(g, x, y, w, 1, P.k); px(g, x, y + 1, w, 2, P.woodLt); px(g, x, y, 1, F, P.k); px(g, x + w - 1, y, 1, F, P.k);
  const wide = w >= 128; // 8+ tile lots get the full layout; narrower ones keep the door, sign, awning and lanterns
  for (const wx of wide ? [x + 16, x + w - 46] : []) {
    px(g, wx, y + 5, 20, 11, P.k); px(g, wx + 1, y + 6, 18, 9, P.amberLt);
    for (let i = 4; i < 18; i += 4) px(g, wx + 1 + i, y + 6, 1, 9, P.amberDk);
    px(g, wx + 1, y + 10, 18, 1, P.amberDk);
    L.push({ x: wx, y: y + 5, w: 20, h: 11, color: P.amber, alpha: 0.22 });
  }
  hSign(g, x + Math.floor((w - hSignW('ラーメン')) / 2), y + 4, 'ラーメン', P.red, '#5a1018', L);
  wallAc(g, x + w - 20, y + 5);
  for (let i = x + 12; i < x + w - 22; i++) px(g, i, y + 17 + (Math.abs(i - (x + w / 2)) < 30 ? 1 : 0), 1, 1, '#05070d');
  vSign(g, x + 2, y - 30, 'ラーメン', P.red, '#3a0a10', L); // hangs at the corner, over the roof edge
  awning(g, x + 1, y + 18, w - 2, P.red, '#f0e2c8', P.redLt);
  // ground floor: lit counter behind noren, menu board, lattice window, vending machine, crates
  const dx = wide ? x + 46 : x + Math.floor(w / 2) - 17;
  px(g, dx, y + 27, 34, 19, P.k); px(g, dx + 1, y + 28, 32, 18, '#ffcf86'); px(g, dx + 1, y + 37, 32, 2, P.woodDk);
  for (let i = 0; i < 5; i++) px(g, dx + 3 + i * 6, y + 41, 3, 4, P.k); // stools
  noren(g, dx + 1, y + 27, 32, P.navy, '#34468a');
  for (const lx of [dx - 8, dx + 36]) lantern(g, lx, y + 28, P.red, P.redDk, L);
  meter(g, x + 6, y + 32);
  if (wide) {
    menuBoard(g, x + 24, y + 30, L);
    const wx = dx + 46; px(g, wx, y + 29, 24, 13, P.k); px(g, wx + 1, y + 30, 22, 11, '#ffb85a');
    for (let i = 3; i < 22; i += 4) px(g, wx + 1 + i, y + 30, 1, 11, P.woodDk);
    px(g, wx + 1, y + 35, 22, 1, P.woodDk);
    L.push({ x: wx, y: y + 29, w: 24, h: 13, color: P.amber, alpha: 0.3 });
  }
  if (w >= 112) {
    vending(g, x + w - 24, y + F - 15, '#c8323c', L);
    for (let i = 0; i < 2; i++) { px(g, x + w - 13, y + 34 + i * 6, 9, 6, P.k); px(g, x + w - 12, y + 35 + i * 6, 7, 4, i ? '#b0302c' : '#c8a020'); }
  }
  px(g, x, y + F - 2, w, 2, P.k);
  L.push({ x: dx, y: y + 27, w: 34, h: 19, color: P.amber, alpha: 0.5 });
  spill(g, m, dx - 10, y + F, 54, P.amber, 0.55);
  wetGlint(g, m, dx - 6, y + F + 6, 46, P.amberLt, 0.5); if (w >= 112) wetGlint(g, m, x + w - 26, y + F + 6, 12, '#ff9a84', 0.4);
}
function karaokeHall(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  px(g, x, y, w, F, '#141a30'); px(g, x, y, w, 1, P.k); px(g, x, y + 1, w, 2, P.rimDk); px(g, x, y, 1, F, P.k); px(g, x + w - 1, y, 1, F, P.k);
  // booth windows with singers' silhouettes, a pink neon trim line and the name board
  const wide = w >= 128, booths = wide ? 5 : Math.max(0, Math.floor((w - hSignW('カラオケ') - 28) / 15));
  for (let wx = x + 6, i = 0; i < booths; wx += 15, i++) {
    const c = [P.cyan, P.magenta, P.violet, P.magenta, P.cyan][i];
    px(g, wx, y + 5, 11, 10, P.k); px(g, wx + 1, y + 6, 9, 8, c); px(g, wx + 4, y + 9, 2, 5, P.k); px(g, wx + 3, y + 7, 4, 2, P.k);
    L.push({ x: wx, y: y + 5, w: 11, h: 10, color: c, alpha: 0.2 });
  }
  hSign(g, x + w - hSignW('カラオケ') - 18, y + 4, 'カラオケ', P.cyan, '#05242c', L);
  px(g, x + 1, y + 16, w - 2, 1, P.magenta); L.push({ x, y: y + 15, w, h: 3, color: P.magenta, alpha: 0.3 });
  vSign(g, x + w - 13, y - 30, 'カラオケ', P.magenta, '#2a0420', L);
  // marquee of bulbs over the entrance
  px(g, x + 1, y + 18, w - 2, 6, '#1a0f24');
  for (let i = x + 2; i < x + w - 2; i += 3) { px(g, i, y + 19, 1, 1, (i >> 1) % 2 ? P.amberLt : P.magenta); px(g, i + 1, y + 22, 1, 1, (i >> 1) % 2 ? P.magenta : P.amberLt); }
  L.push({ x: x + 1, y: y + 18, w: w - 2, h: 6, color: P.amber, alpha: 0.3 });
  // glass ground floor: posters, cyan doors with a pink mic neon above, vending machines, A-frame, plant
  px(g, x + 3, y + 25, w - 6, 21, P.k); px(g, x + 4, y + 26, w - 8, 20, '#0f2a36');
  const dx = wide ? x + 54 : x + Math.floor(w / 2) - 11, posters = wide ? 3 : Math.max(0, Math.floor((dx - x - 20) / 13));
  for (let i = 0; i < posters; i++) { const c = ['#ff4fbc', '#ffe03a', '#45e4f4'][i]; px(g, x + 8 + i * 13, y + 28, 11, 15, '#1d4a5a'); px(g, x + 9 + i * 13, y + 29, 9, 7, c); px(g, x + 9 + i * 13, y + 38, 9, 1, '#9fb8c8'); }
  px(g, dx, y + 26, 22, 20, P.cyan); px(g, dx + 1, y + 27, 20, 19, '#9ff6ff'); px(g, dx + 10, y + 27, 1, 19, P.cyanDk); px(g, dx + 2, y + 38, 18, 8, P.cyanLt);
  stamp(g, ['.##.', '####', '####', '.##.', '..#.', '..#.', '.###'], dx + 9, y + 18, { '#': P.pink }); // mic
  L.push({ x: dx, y: y + 26, w: 22, h: 20, color: P.cyan, alpha: 0.55 }, { x: dx + 8, y: y + 18, w: 6, h: 7, color: P.magenta, alpha: 0.45 });
  plant(g, dx - 10, y + 35);
  if (wide) {
    aFrame(g, dx + 26, y + 36, '#ffd6f0', P.magentaDk);
    vending(g, x + w - 34, y + F - 15, '#2a6ad0', L); vending(g, x + w - 24, y + F - 15, '#e8e8f0', L);
    meter(g, x + w - 12, y + 30);
  } else if (x + w - 14 > dx + 24) vending(g, x + w - 14, y + F - 15, '#2a6ad0', L);
  px(g, x, y + F - 2, w, 2, P.k);
  spill(g, m, dx - 14, y + F, 50, P.cyan, 0.55);
  wetGlint(g, m, dx - 4, y + F + 6, 30, P.cyanLt, 0.5); if (wide) wetGlint(g, m, x + w - 36, y + F + 6, 22, P.pink, 0.35);
}
/** Wet-pavement glint color under each simpler front's door light (none for shuttered shops). */
export const GLINTS: Record<string, string> = {
  arcade: P.pink, karaoke: P.cyanLt, kabuki: P.pink, izakaya: P.amberLt, office: '#cfeaf5', konbini: '#e8f6ff',
  clinic: P.cyanLt, apartment: P.amberLt, ramen: P.amberLt, shop: P.amberLt, hotel: P.pink, bar: P.pink, pachinko: P.amberLt,
};
export const FRONTS: Record<string, typeof arcade> = { arcade, karaoke, kabuki, izakaya, office, konbini, clinic, apartment, shutter, ramen, shop, hotel, bar, pachinko, ramenHouse, karaokeHall };

// ------------------------------------------------------------------ street dressing
/** Paving zone (city-art.json `paving`) a tile lies in: sets the sidewalk material and curb lamp style there. */
export function pavingAt(tx: number, ty: number): string | undefined {
  return CITY_ART.paving.find(z => inTileRect(tx, ty, z))?.style;
}
export const LAMP_PAINTERS: Record<string, (g: G, x: number, y: number, L: Lights) => void> = {};
/** The soft pool of light under a lamp: `rings` stacked translucent discs, `step` px apart. */
export function lampGlow(g: G, x: number, y: number, rings: number, step: number, alpha: number, col: string) {
  for (let r = rings; r >= 1; r--) { g.globalAlpha = alpha * (rings + 1 - r); disc(g, x, y - 2, r * step, col); }
  g.globalAlpha = 1;
}
function streetLamp(g: G, x: number, y: number, L: Lights) {
  lampGlow(g, x, y, 3, 6, 0.05, P.amber);
  px(g, x - 1, y - 1, 3, 3, P.k); px(g, x, y, 1, 1, P.metal);
  px(g, x - 3, y - 7, 7, 4, P.k); px(g, x - 2, y - 6, 5, 2, P.amberLt);
  L.push({ x: x - 4, y: y - 8, w: 9, h: 6, color: P.amber, alpha: 0.4 });
}
function drain(g: G, x: number, y: number) { px(g, x, y, 10, 4, P.k); for (let i = 1; i < 10; i += 2) px(g, x + i, y + 1, 1, 2, '#5d6b80'); }
/** Lantern strings across a pedestrian or nightlife alley (either orientation), with wet glints below. */
function alleyStrings(g: G, a: { x: number; y: number; w: number; h: number }, L: Lights) {
  const vertical = a.h > a.w, len = vertical ? a.h : a.w;
  for (let k = 2; k < len - 2; k += 3) {
    // across the short side: x0..x1 along it, `at` the position along the alley
    const at = (vertical ? a.y : a.x) * TILE + k * TILE + 4, s0 = (vertical ? a.x : a.y) * TILE, s1 = s0 + (vertical ? a.w : a.h) * TILE;
    for (let s = s0; s < s1; s++) { const sag = Math.abs(s - (s0 + s1) / 2) < 8 ? 1 : 0; vertical ? px(g, s, at + sag, 1, 1, '#05070d') : px(g, at + sag, s, 1, 1, '#05070d'); }
    for (let s = s0 + 3, i = 0; s < s1 - 3; s += 6, i++) {
      const col = i % 2 ? P.amber : P.magenta;
      vertical ? lantern(g, s - 1, at + 2, col, P.magentaDk, L, true) : lantern(g, at + 2, s - 2, col, P.magentaDk, L, true);
    }
    g.globalAlpha = 0.18;
    for (let n = 0; n < 4; n++) {
      const off = Math.floor(hash2(k, n + at) * (s1 - s0 - 10)), col = n % 2 ? P.amber : P.magenta;
      vertical ? px(g, s0 + 4 + off, at + 12 + n * 4, 6, 1, col) : px(g, at + 12 + n * 4, s0 + 4 + off, 1, 6, col);
    }
    g.globalAlpha = 1;
  }
}

/** Paint every planned block over the procedural city. Lit parts are appended to `lights` (capped). */
export function paintBlocks(g: G, m: GameMap, lights: Lights) {
  for (const b of planCity(m)) {
    const F = b.facade * TILE, fy = (b.y + b.h - b.facade) * TILE, below = b.y + b.h;
    for (const lot of b.lots) paintLotRoof(g, lot.x * TILE, b.y * TILE, lot.w * TILE, fy - b.y * TILE, lot);
    if (!b.facade) continue;
    // façades after all roofs so tall signs may overhang the roof edge
    for (const lot of b.lots) {
      FRONTS[lot.front]?.(g, m, lot.x * TILE, fy, lot.w * TILE, F, lights);
      const glint = GLINTS[lot.front]; // the two detailed fronts paint their own
      if (glint) wetGlint(g, m, lot.x * TILE + lot.w * 4, fy + F + 6, lot.w * 8, glint, 0.4);
    }
    // a curb lamp and drain in front of each lot where a sidewalk (2 or 3 tiles wide) meets a road
    for (const lot of b.lots) {
      const tx = lot.x + Math.floor(lot.w / 2);
      let k = 1; while (k < 4 && tileAt(m, tx, below + k) === T.SIDEWALK) k++;
      if (k < 2 || tileAt(m, tx, below + k) !== T.ROAD) continue;
      const curb = (below + k) * TILE - 3, lamp = LAMP_PAINTERS[pavingAt(tx, below + 1) ?? ''] ?? streetLamp;
      lamp(g, tx * TILE, curb, lights); drain(g, tx * TILE + 30, curb + 4);
    }
  }
  const strung = CITY_ART.alleyLanterns as Record<string, boolean>;
  for (const a of WORLD.alleys) if (strung[a.name] ?? a.kind === 'foot') alleyStrings(g, a, lights);
}
